/**
 * /coach/messages — Inbox + Thread viewer + Composer.
 *
 * Left rail: thread list (own threads first, then oversight threads marked).
 * Right pane: selected thread's messages + composer.
 *
 * Auto-polls /api/messages/unread-count and the active thread every 8s
 * (cheap polling for v1; WebSockets can come later).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2, Send, Eye, Inbox as InboxIcon, MessageSquare, Search,
    Megaphone, Users, X, ArrowLeft,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import useMessageSocket from "@/hooks/useMessageSocket";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// When the WS is online we slow the poll to 60s as a safety net; live
// events drive the UI. When the WS is offline we fall back to the 8s poll
// the page shipped with.
const POLL_MS_FAST = 8000;
const POLL_MS_SLOW = 60000;

function fmtTime(iso) {
    if (!iso) return "";
    const t = new Date(iso);
    const now = new Date();
    const sameDay = t.toDateString() === now.toDateString();
    if (sameDay) return t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    return t.toLocaleDateString([], { month: "short", day: "numeric" });
}

function fmtRelative(iso) {
    if (!iso) return null;
    const t = new Date(iso);
    if (Number.isNaN(t.getTime())) return null;
    const diffSec = (Date.now() - t.getTime()) / 1000;
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    const days = Math.floor(diffSec / 86400);
    if (days < 7) return `${days}d ago`;
    return t.toLocaleDateString([], { month: "short", day: "numeric" });
}

export default function CoachMessages() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [threads, setThreads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeId, setActiveId] = useState(searchParams.get("t") || null);
    const [activeThread, setActiveThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [sending, setSending] = useState(false);
    const [body, setBody] = useState("");
    const [filter, setFilter] = useState("");
    const [showOversight, setShowOversight] = useState(false);
    const [showMassSend, setShowMassSend] = useState(false);
    const composerRef = useRef(null);
    const messagesListRef = useRef(null);

    const { connected: liveConnected, subscribe } = useMessageSocket();

    const isOwner = user?.role === "owner";

    // Load threads
    const loadThreads = async () => {
        try {
            const r = await axios.get(`${API}/messages/threads`, { withCredentials: true });
            setThreads(r.data?.threads || []);
        } catch (e) {
            toast.error("Couldn't load threads", { description: e?.response?.data?.detail });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadThreads(); }, []);

    // Load active thread + messages
    const loadActive = async (tid) => {
        if (!tid) return;
        try {
            const r = await axios.get(`${API}/messages/threads/${tid}`, { withCredentials: true });
            setActiveThread(r.data?.thread);
            setMessages(r.data?.messages || []);
            // Bump unread to 0 visually
            setThreads((prev) => prev.map((t) => t.thread_id === tid ? { ...t, unread_for_coach: 0 } : t));
        } catch (e) {
            toast.error("Couldn't load thread", { description: e?.response?.data?.detail });
        }
    };

    useEffect(() => {
        if (activeId) {
            loadActive(activeId);
            setSearchParams({ t: activeId });
        }
    }, [activeId]);

    // Poll for new messages — slow when live socket is up, original 8s
    // cadence as a fallback when it's down.
    useEffect(() => {
        const interval = liveConnected ? POLL_MS_SLOW : POLL_MS_FAST;
        const id = setInterval(() => {
            loadThreads();
            if (activeId) loadActive(activeId);
        }, interval);
        return () => clearInterval(id);
    }, [activeId, liveConnected]);

    // Realtime push — append new messages to the open thread, bump
    // unread badges for threads we're not currently viewing.
    useEffect(() => {
        return subscribe((evt) => {
            if (evt.type === "message:new") {
                const tid = evt.thread_id;
                if (tid === activeId) {
                    setMessages((prev) => prev.some((m) => m.message_id === evt.message.message_id)
                        ? prev
                        : [...prev, evt.message],
                    );
                    // Mark read on the server so the badge clears for the
                    // other party — fire-and-forget.
                    if (evt.message.sender_user_id !== user?.user_id) {
                        Promise.resolve().then(() => loadActive(tid));
                    }
                } else {
                    setThreads((prev) => prev.map((t) => t.thread_id === tid
                        ? { ...t, last_message_at: evt.message.sent_at, last_message_preview: evt.message.body, unread_for_coach: (t.unread_for_coach || 0) + 1 }
                        : t,
                    ));
                }
            } else if (evt.type === "thread:read" && evt.thread_id === activeId) {
                const at = new Date().toISOString();
                setMessages((prev) => prev.map((m) =>
                    m.sender_user_id === user?.user_id && !m.read_at_by_recipient
                        ? { ...m, read_at_by_recipient: at }
                        : m,
                ));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeId, subscribe, user?.user_id]);

    // Scroll only the inner messages container to bottom (NOT the page) on new messages
    useEffect(() => {
        const el = messagesListRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages]);

    const sendNow = async () => {
        if (!activeThread || !body.trim()) return;
        setSending(true);
        try {
            await axios.post(`${API}/messages/send`, {
                recipient_user_id: activeThread.client_user_id,
                body,
            }, { withCredentials: true });
            setBody("");
            await loadActive(activeId);
            await loadThreads();
            // Mobile UX (Sachin 2026-02-15): "It disappears when we send it."
            // Coaches replying from a phone want a quick in/out — open thread,
            // reply, dismiss back to the inbox list. On desktop we keep the
            // conversation open since the 2-pane view is the working surface.
            if (typeof window !== "undefined" && window.matchMedia?.("(max-width: 767px)").matches) {
                setActiveId(null);
                setSearchParams((sp) => { sp.delete("t"); return sp; }, { replace: true });
            }
        } catch (e) {
            toast.error("Couldn't send", { description: e?.response?.data?.detail });
        } finally {
            setSending(false);
            composerRef.current?.focus();
        }
    };

    const ownThreads = threads.filter((t) => !t.is_oversight);
    const oversightThreads = threads.filter((t) => t.is_oversight);
    const visibleThreads = useMemo(() => {
        const base = showOversight ? oversightThreads : ownThreads;
        if (!filter.trim()) return base;
        const q = filter.toLowerCase();
        return base.filter((t) =>
            (t.client?.name || "").toLowerCase().includes(q) ||
            (t.client?.email || "").toLowerCase().includes(q) ||
            (t.coach?.name || "").toLowerCase().includes(q) ||
            (t.last_message_preview || "").toLowerCase().includes(q),
        );
    }, [filter, showOversight, ownThreads, oversightThreads]);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-7 h-7 text-brand-red animate-spin" /></div>;
    }

    return (
        <div
            data-testid="coach-messages"
            className="flex h-[calc(100vh-5rem-4rem)] md:h-[calc(100vh-5rem)]"
        >
            {/* Left: thread list — full-width on mobile when no thread is open;
                hidden on mobile when a thread is open (the right pane takes
                over as a full-screen view). md+ always shows the list as a
                fixed 320px rail. (Sachin 2026-02-15 mobile redesign.) */}
            <div
                data-testid="messages-thread-list"
                className={`${activeId ? "hidden md:flex" : "flex"} w-full md:w-80 border-r border-zinc-900 flex-col bg-black`}
            >
                <div className="p-4 border-b border-zinc-900">
                    <div className="font-display text-2xl tracking-wider text-white">INBOX</div>
                    <p className="text-[11px] tracking-wider text-zinc-300 mt-1">{ownThreads.length} thread{ownThreads.length === 1 ? "" : "s"}</p>
                    <div className="relative mt-3">
                        <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Search threads…"
                            data-testid="threads-search"
                            className="w-full bg-zinc-950 border border-zinc-800 pl-10 pr-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red"
                        />
                    </div>
                    {isOwner && (
                        <>
                            <div className="mt-3 grid grid-cols-2 gap-1 text-[10px] tracking-[0.2em] uppercase">
                                <button
                                    type="button"
                                    onClick={() => setShowOversight(false)}
                                    data-testid="tab-mine"
                                    className={`py-2 ${!showOversight ? "bg-brand-red text-white" : "bg-zinc-950 text-zinc-300 hover:text-white"}`}
                                >
                                    <InboxIcon className="w-3 h-3 inline -mt-0.5 mr-1.5" /> Mine ({ownThreads.length})
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowOversight(true)}
                                    data-testid="tab-oversight"
                                    className={`py-2 ${showOversight ? "bg-amber-500 text-black" : "bg-zinc-950 text-zinc-300 hover:text-white"}`}
                                >
                                    <Eye className="w-3 h-3 inline -mt-0.5 mr-1.5" /> Oversight ({oversightThreads.length})
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowMassSend(true)}
                                data-testid="mass-send-open"
                                className="mt-2 w-full py-2 text-[10px] tracking-[0.2em] uppercase bg-zinc-950 hover:bg-zinc-900 border border-zinc-800 text-amber-300 inline-flex items-center justify-center gap-2"
                            >
                                <Megaphone className="w-3 h-3" /> Mass send
                            </button>
                        </>
                    )}
                </div>
                <div className="flex-1 overflow-y-auto">
                    {visibleThreads.length === 0 ? (
                        <div className="p-6 text-center text-sm text-zinc-400">
                            <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-50" />
                            No threads yet.
                        </div>
                    ) : visibleThreads.map((t) => {
                        const unread = t.unread_for_coach || 0;
                        const active = t.thread_id === activeId;
                        return (
                            <button
                                type="button"
                                key={t.thread_id}
                                onClick={() => setActiveId(t.thread_id)}
                                data-testid={`thread-row-${t.thread_id}`}
                                className={`w-full text-left px-4 py-3 border-b border-zinc-900 hover:bg-zinc-950 transition-colors ${active ? "bg-zinc-950 border-l-2 border-l-brand-red" : ""}`}
                            >
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <span className={`text-sm font-semibold truncate ${unread ? "text-white" : "text-zinc-200"}`}>{t.client?.name || "—"}</span>
                                    <span className="text-[10px] text-zinc-400 whitespace-nowrap">{fmtTime(t.last_message_at)}</span>
                                </div>
                                <div className={`text-xs truncate ${unread ? "text-zinc-100" : "text-zinc-400"}`}>{t.last_message_preview || <span className="italic text-zinc-600">No messages yet</span>}</div>
                                <div className="flex items-center gap-2 mt-1.5">
                                    {t.is_oversight && (
                                        <span className="text-[9px] tracking-[0.2em] uppercase bg-amber-950/40 border border-amber-800 text-amber-300 px-1.5 py-0.5">
                                            via {t.coach?.name || "coach"}
                                        </span>
                                    )}
                                    {t.last_read_at_by_client && (
                                        <span
                                            data-testid={`thread-last-seen-${t.thread_id}`}
                                            className="text-[9px] tracking-[0.18em] uppercase text-emerald-400/80"
                                            title={`Parent last opened this thread at ${new Date(t.last_read_at_by_client).toLocaleString()}`}
                                        >
                                            Seen {fmtRelative(t.last_read_at_by_client)}
                                        </span>
                                    )}
                                    {unread > 0 && (
                                        <span data-testid={`thread-unread-${t.thread_id}`} className="ml-auto text-[10px] font-bold bg-brand-red text-white rounded-full px-1.5 min-w-5 h-5 inline-flex items-center justify-center">
                                            {unread}
                                        </span>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Right: thread viewer.
                • Desktop (md+): side-by-side with the list. md:static + md:z-auto
                  override the mobile fixed/z values so the layout stays as a
                  two-pane working surface.
                • Mobile: when a thread is open, the viewer becomes a TRUE
                  full-screen overlay (fixed inset-0 z-[60]) covering BOTH the
                  site navbar AND the bottom coach-mobile-nav. That's what
                  Sachin asked for: "something pops up with the message so we
                  know who we are talking to, and it disappears when we send
                  it." The previous attempt left the header hidden under the
                  fixed navbar (iter57 critical bug). When closed, the viewer
                  is hidden so the list takes the full mobile viewport. */}
            <div
                data-testid="messages-thread-viewer"
                className={
                    activeId
                        ? "fixed inset-0 z-[60] bg-brand-obsidian flex flex-col md:static md:z-auto md:flex md:flex-1 md:min-w-0"
                        : "hidden md:flex md:static md:z-auto md:flex-1 flex-col bg-brand-obsidian md:min-w-0"
                }
            >
                {!activeThread ? (
                    <div className="flex-1 flex items-center justify-center text-zinc-400 text-sm">
                        Pick a thread on the left to open it.
                    </div>
                ) : (
                    <>
                        <div className="border-b border-zinc-900 px-4 md:px-6 py-3 md:py-4 flex items-center gap-3">
                            {/* Mobile-only back arrow: returns to the inbox
                                list without losing scroll/search state. The
                                whole right pane is the modal-like overlay
                                on mobile so this is the dismiss control. */}
                            <button
                                type="button"
                                onClick={() => {
                                    setActiveId(null);
                                    setSearchParams((sp) => { sp.delete("t"); return sp; }, { replace: true });
                                }}
                                data-testid="thread-back"
                                className="md:hidden -ml-2 p-2 text-zinc-200 hover:text-white"
                                aria-label="Back to inbox"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div className="flex-1 min-w-0">
                                <div className="font-display text-lg md:text-xl tracking-wider text-white truncate">{activeThread.client?.name || "—"}</div>
                                <div className="text-[11px] tracking-wider text-zinc-300 mt-0.5 truncate">
                                    {activeThread.client?.email}
                                    {activeThread.client?.person_type && (
                                        <span className="ml-2 inline-block text-[9px] tracking-[0.2em] uppercase bg-zinc-900 border border-zinc-800 px-1.5 py-0.5">{activeThread.client.person_type.replace("_", " ")}</span>
                                    )}
                                    {activeThread.client?.primary_sport && (
                                        <span className="ml-1 inline-block text-[9px] tracking-[0.2em] uppercase bg-zinc-900 border border-zinc-800 px-1.5 py-0.5">{activeThread.client.primary_sport}</span>
                                    )}
                                </div>
                                {activeThread.last_read_at_by_client && (
                                    <div
                                        data-testid="active-thread-last-seen"
                                        className="text-[10px] tracking-[0.2em] uppercase text-emerald-400/80 mt-1"
                                        title={`Parent last opened this thread at ${new Date(activeThread.last_read_at_by_client).toLocaleString()}`}
                                    >
                                        Parent seen {fmtRelative(activeThread.last_read_at_by_client)}
                                    </div>
                                )}
                            </div>
                            {activeThread.is_oversight && (
                                <div className="text-[10px] tracking-[0.2em] uppercase bg-amber-950/40 border border-amber-800 text-amber-300 px-2 py-1">
                                    OWNER VIEW — Coach: {activeThread.coach?.name}
                                </div>
                            )}
                        </div>

                        <div ref={messagesListRef} className="flex-1 overflow-y-auto px-4 md:px-6 py-4 space-y-3">
                            {messages.length === 0 && (
                                <div className="text-center text-sm text-zinc-400 py-10">No messages yet. Say something.</div>
                            )}
                            {messages.map((m) => {
                                const mine = m.sender_user_id === user?.user_id;
                                return (
                                    <div
                                        key={m.message_id}
                                        data-testid={`message-${m.message_id}`}
                                        className={`max-w-[85%] md:max-w-[80%] ${mine ? "ml-auto" : ""}`}
                                    >
                                        <div className={`text-[10px] tracking-wider text-zinc-300 mb-1 ${mine ? "text-right" : ""}`}>
                                            {m.sender_name}
                                            {m.is_owner_intervention && (
                                                <span className="ml-1.5 text-amber-300">· OWNER</span>
                                            )}
                                            <span className="ml-2 text-zinc-500">{fmtTime(m.sent_at)}</span>
                                        </div>
                                        <div
                                            className={`px-3.5 py-2.5 border whitespace-pre-wrap text-sm ${
                                                mine
                                                    ? "bg-brand-red/10 border-brand-red/40 text-white"
                                                    : m.is_owner_intervention
                                                        ? "bg-amber-950/30 border-amber-900 text-white"
                                                        : "bg-zinc-950 border-zinc-800 text-white"
                                            }`}
                                        >
                                            {m.body}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="border-t border-zinc-900 p-3 bg-black flex items-end gap-2">
                            <textarea
                                ref={composerRef}
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                                        e.preventDefault();
                                        sendNow();
                                    }
                                }}
                                rows={2}
                                placeholder={activeThread.is_oversight ? "Post as OWNER — message will be tagged" : "Write a message…   (⌘+Enter to send)"}
                                data-testid="composer-input"
                                className="flex-1 bg-zinc-950 border border-zinc-800 px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-brand-red resize-none"
                            />
                            <button
                                type="button"
                                onClick={sendNow}
                                disabled={sending || !body.trim()}
                                data-testid="composer-send"
                                className="bg-brand-red text-white px-4 py-3 disabled:opacity-40 inline-flex items-center gap-1.5 self-stretch font-semibold tracking-wider text-xs uppercase"
                            >
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                Send
                            </button>
                        </div>
                    </>
                )}
            </div>
            {showMassSend && (
                <MassSendModal onClose={() => setShowMassSend(false)} onSent={() => { setShowMassSend(false); loadThreads(); }} />
            )}
        </div>
    );
}


function MassSendModal({ onClose, onSent }) {
    const [body, setBody] = useState("");
    const [personTypes, setPersonTypes] = useState([]);
    const [sports, setSports] = useState("");
    const [statuses, setStatuses] = useState([]);
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [sending, setSending] = useState(false);

    const togglePT = (v) => setPersonTypes((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]);
    const toggleStatus = (v) => setStatuses((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]);

    const filters = () => ({
        body,
        person_types: personTypes,
        sports: sports.trim() ? sports.split(",").map((x) => x.trim()).filter(Boolean) : [],
        statuses,
        recipient_user_ids: [],
    });

    const runPreview = async () => {
        setPreviewing(true);
        try {
            const r = await axios.post(`${API}/messages/mass-send/preview`, filters(), { withCredentials: true });
            setPreview(r.data);
        } catch (e) {
            toast.error("Preview failed", { description: e?.response?.data?.detail });
        } finally {
            setPreviewing(false);
        }
    };

    const send = async () => {
        if (!body.trim()) { toast.error("Write a message first"); return; }
        if (!preview || preview.audience_count === 0) { toast.error("Run a preview first"); return; }
        if (!window.confirm(`Send to ${preview.audience_count} recipients? This will also email each one a magic-link to reply in MaxSC.`)) return;
        setSending(true);
        try {
            const r = await axios.post(`${API}/messages/mass-send`, filters(), { withCredentials: true });
            toast.success(`Sent to ${r.data.sent_count}`, {
                description: `${r.data.emails_dispatched} emails dispatched · ${r.data.skipped_count} skipped`,
            });
            onSent?.();
        } catch (e) {
            toast.error("Mass-send failed", { description: e?.response?.data?.detail });
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="mass-send-modal" className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
                <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-950">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300 inline-flex items-center gap-1"><Megaphone className="w-3 h-3" /> Mass send</div>
                        <h4 className="font-display text-xl tracking-wider mt-0.5">Owner broadcast</h4>
                    </div>
                    <button type="button" onClick={onClose} data-testid="mass-send-close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-5">
                    <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1 block">Message</label>
                        <textarea
                            value={body}
                            onChange={(e) => { setBody(e.target.value); setPreview(null); }}
                            data-testid="mass-send-body"
                            rows={4}
                            placeholder="Quick reminder for everyone…"
                            className="w-full bg-black border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-2 block">Person type</label>
                        <div className="flex gap-2 flex-wrap">
                            {[
                                ["parent", "Parents"],
                                ["adult_client", "Adult clients"],
                                ["child", "Athlete-mode kids"],
                            ].map(([v, label]) => (
                                <button
                                    key={v}
                                    type="button"
                                    onClick={() => { togglePT(v); setPreview(null); }}
                                    data-testid={`mass-pt-${v}`}
                                    className={`text-xs px-3 py-1.5 rounded border ${personTypes.includes(v) ? "bg-brand-red border-brand-red text-white" : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:text-white"}`}
                                >{label}</button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-2 block">Status</label>
                        <div className="flex gap-2 flex-wrap">
                            {[
                                ["lead", "Leads"],
                                ["member", "Members"],
                                ["inactive", "Inactive"],
                            ].map(([v, label]) => (
                                <button
                                    key={v}
                                    type="button"
                                    onClick={() => { toggleStatus(v); setPreview(null); }}
                                    data-testid={`mass-status-${v}`}
                                    className={`text-xs px-3 py-1.5 rounded border ${statuses.includes(v) ? "bg-brand-red border-brand-red text-white" : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:text-white"}`}
                                >{label}</button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1 block">Sports (comma-separated)</label>
                        <input
                            type="text"
                            value={sports}
                            onChange={(e) => { setSports(e.target.value); setPreview(null); }}
                            data-testid="mass-sports"
                            placeholder="e.g. football, basketball, soccer"
                            className="w-full bg-black border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red"
                        />
                    </div>
                    <div className="border-t border-zinc-800 pt-4 flex items-center justify-between gap-2">
                        <button
                            type="button"
                            onClick={runPreview}
                            disabled={previewing}
                            data-testid="mass-send-preview"
                            className="text-xs px-3 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 inline-flex items-center gap-2 disabled:opacity-50"
                        >
                            {previewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Users className="w-3 h-3" />}
                            Preview audience
                        </button>
                        {preview && (
                            <div data-testid="mass-send-preview-result" className="text-xs text-zinc-300">
                                <span className="text-white font-semibold">{preview.audience_count}</span> recipient{preview.audience_count === 1 ? "" : "s"}
                            </div>
                        )}
                    </div>
                    {preview?.sample?.length > 0 && (
                        <div className="text-[11px] text-zinc-500 -mt-2">
                            Sample: {preview.sample.slice(0, 5).map((s) => s.name).join(", ")}{preview.sample.length > 5 ? "…" : ""}
                        </div>
                    )}
                </div>
                <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2 sticky bottom-0 bg-zinc-950">
                    <button type="button" onClick={onClose} className="text-xs px-4 py-2 text-zinc-300 hover:text-white">Cancel</button>
                    <button
                        type="button"
                        onClick={send}
                        disabled={sending || !preview || preview.audience_count === 0 || !body.trim()}
                        data-testid="mass-send-confirm"
                        className="text-xs px-4 py-2 bg-brand-red text-white disabled:opacity-40 inline-flex items-center gap-2"
                    >
                        {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        Send to {preview?.audience_count ?? 0}
                    </button>
                </div>
            </div>
        </div>
    );
}
